import { useOnFirstMount } from '@insify/react-hooks';
import React from 'react';

import { getPreviewFromUrlOrSessionStorage } from './getPreviewFromUrlOrSessionStorage';

type ContentForEveryPromotion<PromotionName, ContentType> = Record<
  Exclude<PromotionName extends string ? PromotionName : undefined, undefined>,
  ContentType
>;

type PropsUsePromotionProvider<PromotionName, ContentType> = {
  children: React.ReactNode;
  allContent: ContentType;
  onMountPromotion: (promotion: Exclude<PromotionName, undefined>) => void;
};

type UsePromotion<PromotionName, ContentType> =
  | {
      isEnabled: true;
      name: PromotionName;
      content: ContentType;
    }
  | {
      isEnabled: false;
    };

/**
 * @example
 * const { usePromotion, PromotionProvider } = createUsePromotion<{ name: 'radio' | 'winteractie' }, Content>({
 *  name: 'radio,
 * });
 */

type ParamsCreateUsePromotion<PromotionName> = {
  isPreviewAvailable: boolean;
  name?: PromotionName;
};
export const createUsePromotion = <PromotionName, ContentType>(params: ParamsCreateUsePromotion<PromotionName>) => {
  type AllContent = ContentForEveryPromotion<PromotionName, ContentType>;
  const usePromotionContext = React.createContext<UsePromotion<PromotionName, ContentType> | undefined>(undefined);

  /**
   * @example
   * const { isEnabled, name, content } = usePromotion();
   */
  const usePromotion = (): UsePromotion<PromotionName, ContentType> => {
    const context = React.useContext(usePromotionContext);

    if (context === undefined) {
      throw new Error('usePromotion must be used within a PromotionProvider');
    }

    if (!context.isEnabled) {
      return {
        isEnabled: false,
      };
    }

    return context;
  };

  /**
   * @example
   * <PromotionProvider
   *   content={{
   *     'insify-radio': {
   *       promotionExplained: {
   *         title: t('disability:promotion_explained_radio_title'),
   *         description: t('disability:promotion_explained_radio_description'),
   *       },
   *     },
   *   }}
   *   onMountPromotion={(name) => sendUsePartnerCommand(name)}
   * >
   *  {children}
   * </PromotionProvider>
   */
  const PromotionProvider = (props: PropsUsePromotionProvider<PromotionName, AllContent>) => {
    const isEnabledByEnvironment = typeof params.name === 'string';
    const [isPreviewEnabled, previewName] = getPreviewFromUrlOrSessionStorage<PromotionName>({
      queryKey: 'promotion',
      isEnabled: params.isPreviewAvailable,
    });

    const isEnabled = isEnabledByEnvironment || isPreviewEnabled;
    const promotionName = (params.name ?? previewName) as PromotionName;

    useOnFirstMount(() => {
      if (isEnabled) {
        void props.onMountPromotion((params.name ?? previewName) as Exclude<PromotionName, undefined>);
      }
    });

    return (
      <usePromotionContext.Provider
        value={
          isEnabled
            ? {
                isEnabled: true,
                name: promotionName,
                content: props.allContent[promotionName as keyof AllContent],
              }
            : {
                isEnabled: false,
              }
        }
      >
        {props.children}
      </usePromotionContext.Provider>
    );
  };
  return {
    usePromotion,
    PromotionProvider,
  };
};
